import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { brl, parseDec } from "@/lib/format";
import { ChevronLeft, ChevronRight, Plus, Trash2, Info, Save } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useSearchParams } from "react-router-dom";

const COLORS = ["#682F3E", "#C98F96", "#EBD8D2", "#B76E56", "#4F7A61"];

const emptyMaterial = () => ({ name: "", package_price: "", package_quantity: "", used_quantity: "", unit: "g" });

const DEMO_PRICING = {
  "Alongamento em Gel": {
    materials: [
      { name: "Gel construtor", package_price: "119.90", package_quantity: "30", used_quantity: "3", unit: "g" },
      { name: "Gel base", package_price: "89.90", package_quantity: "30", used_quantity: "2.5", unit: "g" },
      { name: "Primer", package_price: "35.90", package_quantity: "15", used_quantity: "0.5", unit: "ml" },
      { name: "Top coat", package_price: "49.90", package_quantity: "15", used_quantity: "0.6", unit: "ml" },
    ],
    disposables: [
      { name: "Lixa", package_price: "25.00", package_quantity: "10", used_quantity: "1", unit: "unidade" },
      { name: "Palito", package_price: "12.00", package_quantity: "100", used_quantity: "1", unit: "unidade" },
      { name: "Luvas", package_price: "34.90", package_quantity: "50", used_quantity: "1", unit: "par" },
      { name: "Algodão", package_price: "18.90", package_quantity: "500", used_quantity: "4", unit: "unidade" },
    ],
    fixed: "1350", productiveHoursWeek: "30", desiredMonthlyPay: "4500", waste: "5", fee: "4.99", margin: "25", monthly: 60,
  },
  "Manutenção de Gel": {
    materials: [
      { name: "Gel construtor", package_price: "119.90", package_quantity: "30", used_quantity: "1.8", unit: "g" },
      { name: "Primer", package_price: "35.90", package_quantity: "15", used_quantity: "0.4", unit: "ml" },
      { name: "Top coat", package_price: "49.90", package_quantity: "15", used_quantity: "0.5", unit: "ml" },
    ],
    disposables: [
      { name: "Lixa", package_price: "25.00", package_quantity: "10", used_quantity: "1", unit: "unidade" },
      { name: "Luvas", package_price: "34.90", package_quantity: "50", used_quantity: "1", unit: "par" },
      { name: "Algodão", package_price: "18.90", package_quantity: "500", used_quantity: "3", unit: "unidade" },
    ],
    fixed: "1350", productiveHoursWeek: "30", desiredMonthlyPay: "4500", waste: "5", fee: "4.99", margin: "25", monthly: 60,
  },
  "Banho de Gel": {
    materials: [
      { name: "Gel base", package_price: "89.90", package_quantity: "30", used_quantity: "1.6", unit: "g" },
      { name: "Gel construtor", package_price: "119.90", package_quantity: "30", used_quantity: "1.2", unit: "g" },
      { name: "Top coat", package_price: "49.90", package_quantity: "15", used_quantity: "0.5", unit: "ml" },
    ],
    disposables: [
      { name: "Lixa", package_price: "25.00", package_quantity: "10", used_quantity: "1", unit: "unidade" },
      { name: "Luvas", package_price: "34.90", package_quantity: "50", used_quantity: "1", unit: "par" },
    ],
    fixed: "1350", productiveHoursWeek: "30", desiredMonthlyPay: "4500", waste: "5", fee: "4.99", margin: "25", monthly: 60,
  },
  "Esmaltação em Gel": {
    materials: [
      { name: "Base em gel", package_price: "39.90", package_quantity: "10", used_quantity: "0.5", unit: "ml" },
      { name: "Esmalte em gel", package_price: "42.90", package_quantity: "10", used_quantity: "0.8", unit: "ml" },
      { name: "Top coat", package_price: "49.90", package_quantity: "15", used_quantity: "0.5", unit: "ml" },
    ],
    disposables: [
      { name: "Lixa", package_price: "25.00", package_quantity: "10", used_quantity: "1", unit: "unidade" },
      { name: "Algodão", package_price: "18.90", package_quantity: "500", used_quantity: "3", unit: "unidade" },
    ],
    fixed: "1350", productiveHoursWeek: "30", desiredMonthlyPay: "4500", waste: "5", fee: "4.99", margin: "25", monthly: 60,
  },
  "Nail Art Premium": {
    materials: [
      { name: "Gel paint", package_price: "45.00", package_quantity: "5", used_quantity: "0.4", unit: "g" },
      { name: "Glitter / foil", package_price: "28.00", package_quantity: "10", used_quantity: "0.3", unit: "g" },
      { name: "Top coat", package_price: "49.90", package_quantity: "15", used_quantity: "0.4", unit: "ml" },
    ],
    disposables: [
      { name: "Pincel descartável", package_price: "19.90", package_quantity: "20", used_quantity: "1", unit: "unidade" },
      { name: "Algodão", package_price: "18.90", package_quantity: "500", used_quantity: "2", unit: "unidade" },
    ],
    fixed: "1350", productiveHoursWeek: "30", desiredMonthlyPay: "4500", waste: "5", fee: "4.99", margin: "30", monthly: 60,
  },
};

export default function Precificacao() {
  const [params] = useSearchParams();
  const preSvc = params.get("service");
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(preSvc || "");
  const [currentPrice, setCurrentPrice] = useState("");
  const [durationHours, setDurationHours] = useState("2");
  const [durationMinutes, setDurationMinutes] = useState("0");
  const [monthlyAppts, setMonthlyAppts] = useState("");
  const [materials, setMaterials] = useState([emptyMaterial()]);
  const [disposables, setDisposables] = useState([emptyMaterial()]);
  const [otherCosts, setOtherCosts] = useState("");
  const [fixed, setFixed] = useState("");
  const [productiveHoursWeek, setProductiveHoursWeek] = useState("");
  const [desiredMonthlyPay, setDesiredMonthlyPay] = useState("");
  const [wastePct, setWastePct] = useState("0");
  const [fee, setFee] = useState("0");
  const [tax, setTax] = useState("0");
  const [margin, setMargin] = useState("20");
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState("");

  const applyService = (svc) => {
    if (!svc) return;
    setServiceId(svc.id);
    setCurrentPrice(String(svc.price || ""));
    const totalMinutes = Math.max(1, Math.round(Number(svc.duration_minutes) || 60));
    setDurationHours(String(Math.floor(totalMinutes / 60)));
    setDurationMinutes(String(totalMinutes % 60));

    const demo = svc.demo ? DEMO_PRICING[svc.name] : null;
    if (demo) {
      setMaterials(demo.materials);
      setDisposables(demo.disposables);
      setOtherCosts(demo.other || "4");
      setFixed(demo.fixed);
      setProductiveHoursWeek(demo.productiveHoursWeek || "30");
      setDesiredMonthlyPay(demo.desiredMonthlyPay || "3000");
      setWastePct(demo.waste || "5");
      setFee(demo.fee);
      setTax(demo.tax || "0");
      setMargin(demo.margin);
      setMonthlyAppts(demo.monthly);
    } else {
      setMaterials([emptyMaterial()]);
      setDisposables([emptyMaterial()]);
      setOtherCosts("");
      setFixed("");
      setProductiveHoursWeek("");
      setDesiredMonthlyPay("");
      setWastePct("0");
      setFee("0");
      setTax("0");
      setMargin("20");
      setMonthlyAppts("");
    }
    setResult(null);
    setCalculationError("");
  };

  useEffect(() => {
    api.get("/services").then(r => {
      setServices(r.data);
      const selected = r.data.find(s => s.id === preSvc) || r.data[0];
      if (selected) applyService(selected);
    });
  // eslint-disable-next-line
  }, []);

  const durationTotalHours = Math.max(0, parseDec(durationHours)) + (Math.max(0, parseDec(durationMinutes)) / 60);

  const payload = useMemo(() => ({
    service_id: serviceId,
    current_price: parseDec(currentPrice),
    duration_hours: durationTotalHours,
    monthly_appointments: Math.max(0, Math.round(parseDec(monthlyAppts))),
    materials: materials.filter(m => m.name).map(m => ({
      name: m.name, package_price: parseDec(m.package_price),
      package_quantity: parseDec(m.package_quantity), used_quantity: parseDec(m.used_quantity),
      unit: m.unit,
    })),
    disposables: disposables.filter(m => m.name).map(m => ({
      name: m.name, package_price: parseDec(m.package_price),
      package_quantity: parseDec(m.package_quantity), used_quantity: parseDec(m.used_quantity),
      unit: m.unit,
    })),
    other_costs_per_appointment: parseDec(otherCosts),
    monthly_fixed_costs: parseDec(fixed),
    productive_hours_per_week: parseDec(productiveHoursWeek),
    desired_monthly_pay: parseDec(desiredMonthlyPay),
    waste_pct: parseDec(wastePct),
    machine_fee_pct: parseDec(fee),
    tax_pct: parseDec(tax),
    margin_pct: parseDec(margin),
  }), [serviceId, currentPrice, durationTotalHours, monthlyAppts, materials, disposables, otherCosts, fixed, productiveHoursWeek, desiredMonthlyPay, wastePct, fee, tax, margin]);

  const compute = async () => {
    setCalculating(true);
    setCalculationError("");
    try {
      const r = await api.post("/pricing/calculate", payload);
      setResult(r.data);
    } catch (e) {
      const message = apiError(e);
      setResult(null);
      setCalculationError(message);
      toast.error(message);
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    if (step === 9) compute();
    // eslint-disable-next-line
  }, [step, payload]);

  const save = async () => {
    if (!serviceId) { toast.error("Selecione um serviço."); return; }
    try { await api.post("/pricing/save", payload); toast.success("Precificação salva!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const stepLabels = ["Serviço", "Materiais", "Descartáveis", "Outros custos", "Seu negócio", "Seu salário", "Taxas e impostos", "Lucro", "Resultado"];

  const validateCostItems = (items, label) => {
    for (const item of items.filter(x => x.name?.trim())) {
      const price = parseDec(item.package_price);
      const packageQty = parseDec(item.package_quantity);
      const usedQty = parseDec(item.used_quantity);

      if (price < 0 || packageQty < 0 || usedQty < 0) {
        toast.error(`Confira ${item.name}: os valores não podem ser negativos.`);
        return false;
      }
      if (usedQty > 0 && price <= 0) {
        toast.error(`Confira ${item.name}: informe quanto você pagou na embalagem.`);
        return false;
      }
      if (usedQty > 0 && packageQty <= 0) {
        toast.error(`Confira ${item.name}: informe quanto vem na embalagem.`);
        return false;
      }
      if (packageQty > 0 && usedQty > packageQty) {
        toast.error(`Confira ${item.name}: a quantidade usada está maior que a quantidade da embalagem.`);
        return false;
      }
    }
    return true;
  };

  const goNext = () => {
    if (step === 1) {
      if (!serviceId) return toast.error("Selecione um serviço.");
      const hours = parseDec(durationHours);
      const minutes = parseDec(durationMinutes);
      if (!Number.isInteger(hours) || hours < 0) return toast.error("Informe as horas usando um número inteiro.");
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) return toast.error("Os minutos precisam ficar entre 0 e 59.");
      if (durationTotalHours <= 0) return toast.error("Informe a duração do serviço.");
      if (durationTotalHours > 12) return toast.error("Confira a duração: um atendimento não pode ultrapassar 12 horas.");
      if (parseDec(monthlyAppts) < 0) return toast.error("A quantidade de atendimentos não pode ser negativa.");
    }
    if (step === 2) {
      if (!validateCostItems(materials, "material")) return;
      if (parseDec(wastePct) < 0 || parseDec(wastePct) > 50) {
        return toast.error("A reserva para perdas precisa ficar entre 0% e 50%.");
      }
    }
    if (step === 3 && !validateCostItems(disposables, "descartável")) return;
    if (step === 5 && parseDec(productiveHoursWeek) <= 0) {
      return toast.error("Informe quantas horas por semana você costuma passar atendendo clientes.");
    }
    if (step === 6) {
      if (String(desiredMonthlyPay).trim() === "") {
        return toast.error("Informe quanto você gostaria de receber por mês. Se não quiser incluir remuneração, coloque 0.");
      }
      if (parseDec(desiredMonthlyPay) < 0) {
        return toast.error("O valor que você deseja receber não pode ser negativo.");
      }
    }
    if (step === 7 && parseDec(fee) + parseDec(tax) >= 100) {
      return toast.error("Taxas e impostos juntos precisam ser menores que 100%.");
    }
    if (step === 8 && parseDec(fee) + parseDec(tax) + parseDec(margin) >= 100) {
      return toast.error("Taxas, impostos e margem juntos precisam ser menores que 100%.");
    }
    setStep(s => Math.min(9, s + 1));
  };

  return (
    <div className="space-y-5" data-testid="pricing-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Precificação</h1>
        <p className="text-[#71717A] text-sm mt-1">Descubra quanto cobrar por cada serviço com base nos seus custos reais.</p>
      </div>

      <div className="flex gap-1 overflow-x-auto hide-scrollbar" data-testid="pricing-steps">
        {stepLabels.map((l, i) => (
          <button key={l} onClick={() => setStep(i + 1)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${step === i + 1 ? "bg-[#682F3E] text-white" : "bg-white text-[#71717A] border border-[#E4E4E7]"}`}>
            {i + 1}. {l}
          </button>
        ))}
      </div>

      <div className="card-nc p-5 space-y-4">
        {step === 1 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Sobre o serviço</h2>
            <SelectField
              label="Serviço"
              value={serviceId}
              onChange={(id) => applyService(services.find(s => s.id === id))}
              tid="p-service"
            >
              <option value="">Selecione…</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.name}{s.demo ? " · exemplo" : ""}</option>)}
            </SelectField>
            {services.find(s => s.id === serviceId)?.demo && (
              <div className="p-3 rounded-xl bg-[#EBF2EE] text-sm text-[#365F48]">
                <b>Exemplo preenchido:</b> avance pelas etapas para ver materiais, descartáveis, estrutura, remuneração, taxas e lucro usados nesta simulação fictícia.
              </div>
            )}
            <NumField label="Preço atual (R$)" value={currentPrice} onChange={setCurrentPrice} tid="p-current-price" />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">Duração do serviço</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <input inputMode="numeric" value={durationHours} onChange={e => setDurationHours(e.target.value)} className="input-nc" data-testid="p-duration-hours" />
                  <div className="text-[11px] text-[#817A7D] mt-1">Horas</div>
                </div>
                <div>
                  <input inputMode="numeric" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} className="input-nc" data-testid="p-duration-minutes" />
                  <div className="text-[11px] text-[#817A7D] mt-1">Minutos (0 a 59)</div>
                </div>
              </div>
              <p className="text-xs text-[#71717A] flex items-start gap-1 mt-2">
                <Info size={12} className="mt-0.5" /> Exemplo: para 1h40, informe 1 em horas e 40 em minutos.
              </p>
            </div>
            <NumField label="Atendimentos deste serviço por mês" value={monthlyAppts} onChange={setMonthlyAppts} tid="p-monthly" />
            <p className="text-xs text-[#71717A] flex items-start gap-1"><Info size={12} className="mt-0.5" /> Este número é usado apenas para mostrar projeções mensais. Seus custos fixos serão distribuídos pelas horas de atendimento, não pela quantidade de clientes.</p>
          </>
        )}
        {step === 2 && (
          <>
            <MaterialsStep title="Materiais" items={materials} setItems={setMaterials} tid="mat" />
            <div className="pt-2">
              <NumField label="Reserva para perdas e desperdícios (%)" value={wastePct} onChange={setWastePct} tid="p-waste" />
              <p className="text-xs text-[#71717A] flex items-start gap-1 mt-2">
                <Info size={12} className="mt-0.5" /> Começamos em 0%. Se você costuma ter perdas de produto, pode adicionar uma pequena reserva, como 5%.
              </p>
            </div>
          </>
        )}
        {step === 3 && <MaterialsStep title="Descartáveis" items={disposables} setItems={setDisposables} tid="disp" />}
        {step === 4 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Outros custos por atendimento</h2>
            <p className="text-sm text-[#71717A]">
              Inclua gastos que não entram nos materiais: café/água da cliente, esterilização, lavanderia, embalagem, estacionamento ou outro custo direto.
            </p>
            <NumField label="Outros custos por atendimento (R$)" value={otherCosts} onChange={setOtherCosts} tid="p-other-costs" />
            <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
              Se não tiver nenhum custo extra, deixe em 0.
            </div>
          </>
        )}
        {step === 5 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Seu negócio</h2>
            <p className="text-sm text-[#71717A]">
              Primeiro calculamos quanto custa manter sua estrutura aberta por hora de atendimento. Assim, um serviço de 2 horas recebe uma parte maior dos custos fixos do que um serviço de 30 minutos.
            </p>
            <NumField label="Custos fixos do negócio (R$/mês)" value={fixed} onChange={setFixed} tid="p-fixed" />
            <NumField label="Horas por semana atendendo clientes" value={productiveHoursWeek} onChange={setProductiveHoursWeek} tid="p-productive-hours" />
            <p className="text-xs text-[#71717A] flex items-start gap-1">
              <Info size={12} className="mt-0.5" /> Conte somente as horas em que você costuma estar atendendo clientes, e não todo o período em que fica no salão.
            </p>
            {parseDec(productiveHoursWeek) > 0 && (
              <div className="p-3 rounded-xl bg-[#F5ECE8] text-sm text-[#682F3E] space-y-1">
                <div>Isso representa cerca de <b>{(parseDec(productiveHoursWeek) * 52 / 12).toFixed(1)} horas produtivas por mês</b>.</div>
                {parseDec(fixed) > 0 && (
                  <div>
                    Sua estrutura custa aproximadamente <b>{brl(parseDec(fixed) / (parseDec(productiveHoursWeek) * 52 / 12))} por hora atendida</b>.
                    Neste serviço, a parte da estrutura será de <b>{brl((parseDec(fixed) / (parseDec(productiveHoursWeek) * 52 / 12)) * durationTotalHours)}</b>.
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {step === 6 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Quanto você quer receber pelo seu trabalho?</h2>
            <p className="text-sm text-[#71717A]">
              Pense nesse valor como o seu salário: quanto você gostaria de tirar do negócio para você todos os meses pelo trabalho que realiza.
            </p>
            <NumField label="Quanto você gostaria de receber por mês (R$)" value={desiredMonthlyPay} onChange={setDesiredMonthlyPay} tid="p-desired-pay" />
            {parseDec(desiredMonthlyPay) > 0 && parseDec(productiveHoursWeek) > 0 && (
              <div className="p-3 rounded-xl bg-[#EBF2EE] text-sm text-[#365F48] space-y-1">
                <div>
                  Para essa meta, cada hora atendida precisa remunerar seu trabalho em cerca de <b>{brl(parseDec(desiredMonthlyPay) / (parseDec(productiveHoursWeek) * 52 / 12))}</b>.
                </div>
                <div>
                  Neste serviço, sua remuneração pelo tempo será de <b>{brl((parseDec(desiredMonthlyPay) / (parseDec(productiveHoursWeek) * 52 / 12)) * durationTotalHours)}</b>.
                </div>
              </div>
            )}
            <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
              Seu salário e o lucro do negócio são coisas diferentes. Primeiro o preço precisa remunerar o seu trabalho; depois calculamos o lucro da empresa.
            </div>
          </>
        )}
        {step === 7 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Taxas e impostos</h2>
            <p className="text-sm text-[#71717A]">Informe percentuais que são descontados da venda. Se não houver, use 0.</p>
            <NumField label="Taxa da maquininha / pagamento (%)" value={fee} onChange={setFee} tid="p-fee" />
            <NumField label="Impostos sobre a venda (%)" value={tax} onChange={setTax} tid="p-tax" />
            <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
              Exemplo: se a maquininha cobra 4,99%, informe 4,99. Se você não paga imposto percentual sobre cada venda, informe 0.
            </div>
          </>
        )}
        {step === 8 && (
          <>
            <h2 className="font-display text-lg font-bold text-[#27272A]">Lucro do negócio</h2>
            <p className="text-sm text-[#71717A]">
              Agora escolha quanto você gostaria que sobrasse como lucro da empresa depois de pagar produtos, estrutura, seu trabalho, taxas e impostos.
            </p>
            <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
              Exemplo: 20% significa que, de cada R$ 100 cobrados, aproximadamente R$ 20 devem sobrar como lucro depois de todos os custos e descontos.
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[10, 20, 30, 40].map(p => (
                <button key={p} onClick={() => setMargin(String(p))} data-testid={`preset-${p}`}
                  className={`p-2 rounded-xl border-2 text-sm font-semibold ${parseDec(margin) === p ? "border-[#682F3E] bg-[#F5ECE8]" : "border-[#E4E4E7]"}`}>
                  {p}%
                </button>
              ))}
            </div>
            <NumField label="Margem de lucro (%)" value={margin} onChange={setMargin} tid="p-margin" />
          </>
        )}
        {step === 9 && (
          calculating ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 mx-auto rounded-full border-4 border-[#EBD8D2] border-t-[#682F3E] animate-spin" />
              <div className="font-semibold text-[#682F3E] mt-4">Calculando seu preço recomendado…</div>
            </div>
          ) : result ? (
            <ResultView result={result} onSave={save} />
          ) : (
            <div className="py-8 text-center">
              <div className="font-semibold text-[#B76E56]">{calculationError || "Não foi possível calcular o preço."}</div>
              <button onClick={compute} className="btn-primary-nc mt-4">Tentar novamente</button>
            </div>
          )
        )}

        <div className="flex items-center justify-between pt-2">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
            className="text-[#71717A] flex items-center gap-1 text-sm font-medium disabled:opacity-30" data-testid="p-prev">
            <ChevronLeft size={16} /> Voltar
          </button>
          {step < 9 ? (
            <button onClick={goNext} className="btn-primary-nc flex items-center gap-1 text-sm" data-testid="p-next">
              {step === 8 ? "Calcular preço recomendado" : "Avançar"} <ChevronRight size={16} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MaterialsStep({ title, items, setItems, tid }) {
  const upd = (i, k, v) => setItems(list => list.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add = () => setItems(list => [...list, emptyMaterial()]);
  const rem = (i) => setItems(list => list.filter((_, idx) => idx !== i));

  const itemCost = (item) => {
    const price = parseDec(item.package_price);
    const packageQty = parseDec(item.package_quantity);
    const usedQty = parseDec(item.used_quantity);
    if (price <= 0 || packageQty <= 0 || usedQty <= 0) return 0;
    return (price / packageQty) * usedQty;
  };

  const subtotal = items.reduce((sum, item) => sum + itemCost(item), 0);

  return (
    <>
      <h2 className="font-display text-lg font-bold text-[#27272A]">{title}</h2>
      <p className="text-sm text-[#71717A]">
        Para cada item, informe o valor da embalagem, quanto vem nela e quanto você usa em um único atendimento.
      </p>
      <div className="p-3 rounded-xl bg-[#F5ECE8] text-xs text-[#682F3E]">
        Exemplo: um gel de R$ 60 com 30 g, usando 2 g por cliente, custa <b>R$ 4 por atendimento</b>.
      </div>

      <div className="space-y-3">
        {items.map((m, i) => {
          const cost = itemCost(m);
          const packageQty = parseDec(m.package_quantity);
          const usedQty = parseDec(m.used_quantity);
          const suspicious = packageQty > 0 && usedQty > packageQty;

          return (
            <div key={i} className="p-4 rounded-xl border border-[#E4E4E7] space-y-3" data-testid={`${tid}-item-${i}`}>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <label className="block text-[11px] font-semibold text-[#71717A] mb-1">Produto / item</label>
                  <input placeholder="Ex.: Gel construtor" value={m.name} onChange={e => upd(i, "name", e.target.value)} className="input-nc w-full" data-testid={`${tid}-name-${i}`} />
                </div>
                <button onClick={() => rem(i)} className="text-[#B76E56] p-2 mt-5" aria-label="Remover item"><Trash2 size={16} /></button>
              </div>

              <div className="grid sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-[#71717A] mb-1">Quanto pagou na embalagem?</label>
                  <input inputMode="decimal" placeholder="R$ 60,00" value={m.package_price} onChange={e => upd(i, "package_price", e.target.value)} className="input-nc w-full" data-testid={`${tid}-pp-${i}`} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#71717A] mb-1">Quanto vem na embalagem?</label>
                  <input inputMode="decimal" placeholder="30" value={m.package_quantity} onChange={e => upd(i, "package_quantity", e.target.value)} className="input-nc w-full" data-testid={`${tid}-pq-${i}`} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#71717A] mb-1">Quanto usa por atendimento?</label>
                  <input inputMode="decimal" placeholder="2" value={m.used_quantity} onChange={e => upd(i, "used_quantity", e.target.value)} className="input-nc w-full" data-testid={`${tid}-uq-${i}`} />
                </div>
              </div>

              <div className="grid sm:grid-cols-[160px_1fr] gap-2 items-end">
                <div>
                  <label className="block text-[11px] font-semibold text-[#71717A] mb-1">Unidade</label>
                  <select value={m.unit} onChange={e => upd(i, "unit", e.target.value)} className="input-nc w-full">
                    <option>g</option><option>ml</option><option>unidade</option><option>par</option><option>folha</option>
                  </select>
                </div>
                <div className={`rounded-xl p-3 text-sm ${suspicious ? "bg-[#F9ECE8] text-[#8E4736]" : "bg-[#EBF2EE] text-[#365F48]"}`}>
                  {suspicious ? (
                    <span><b>Confira as quantidades.</b> Você informou que usa mais do que vem em uma embalagem.</span>
                  ) : cost > 0 ? (
                    <span>Custo deste item no atendimento: <b>{brl(cost)}</b></span>
                  ) : (
                    <span>Preencha os três valores para ver o custo deste item por atendimento.</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {subtotal > 0 && (
        <div className="rounded-xl bg-[#FAF8F6] border border-[#E8E2DE] p-3 flex items-center justify-between text-sm">
          <span className="font-semibold text-[#27272A]">Subtotal de {title.toLowerCase()}</span>
          <span className="font-mono-brand font-bold text-[#682F3E]">{brl(subtotal)}</span>
        </div>
      )}

      <button onClick={add} className="text-[#682F3E] font-semibold text-sm flex items-center gap-1" data-testid={`${tid}-add`}>
        <Plus size={16} /> Adicionar item
      </button>
    </>
  );
}

function ResultView({ result, onSave }) {
  const recommended = result.recommended_price ?? result.calculated_price ?? 0;
  const calculatedRaw = result.calculated_price_raw ?? recommended;
  const minimum = result.minimum_price ?? result.base_cost ?? 0;
  const current = result.current_price || 0;
  const difference = result.difference ?? (recommended - current);
  const differencePct = result.difference_pct;
  const monthlyImpact = result.estimated_monthly_revenue_difference ?? 0;
  const monthlyQty = result.monthly_appointments ?? 0;

  const materials = result.materials_cost || 0;
  const disposables = result.disposables_cost || 0;
  const other = result.other_costs || 0;
  const structure = result.fixed_cost_for_service ?? result.fixed_per_appointment ?? 0;
  const labor = result.labor_cost || 0;
  const feesTaxes = (result.fee_value || 0) + (result.tax_value || 0);
  const profit = result.profit_per_appointment ?? result.margin_value ?? 0;
  const productsAndDirect = materials + disposables + other;
  const warnings = result.warnings || [];
  const materialBreakdown = result.materials_breakdown || [];
  const disposableBreakdown = result.disposables_breakdown || [];

  const chartData = [
    { name: "Produtos e insumos", value: productsAndDirect },
    { name: "Estrutura", value: structure },
    { name: "Seu trabalho", value: labor },
    { name: "Taxas e impostos", value: feesTaxes },
    { name: "Lucro do negócio", value: profit },
  ].filter(x => x.value > 0);

  const per100 = recommended > 0 ? [
    { name: "Produtos e insumos", value: (productsAndDirect / recommended) * 100 },
    { name: "Estrutura do negócio", value: (structure / recommended) * 100 },
    { name: "Seu trabalho", value: (labor / recommended) * 100 },
    { name: "Taxas e impostos", value: (feesTaxes / recommended) * 100 },
    { name: "Lucro do negócio", value: (profit / recommended) * 100 },
  ] : [];

  let comparison = null;
  if (current > 0) {
    const pctText = differencePct == null ? "" : ` (${Math.abs(differencePct).toFixed(1).replace(".", ",")}%)`;

    if (Math.abs(difference) < 0.01) {
      comparison = {
        tone: "bg-[#EBF2EE] text-[#365F48] border-[#CFE0D5]",
        title: "Seu preço atual está alinhado ao valor sugerido.",
        text: "Pelos dados informados, não há diferença relevante entre os dois valores.",
      };
    } else if (difference > 0) {
      comparison = {
        tone: current < minimum
          ? "bg-[#F9ECE8] text-[#8E4736] border-[#E9C9BE]"
          : "bg-[#FFF7E8] text-[#7A5B21] border-[#F0DFB9]",
        title: `Seu preço atual está ${brl(Math.abs(difference))} abaixo do preço sugerido.`,
        text: `Para chegar ao valor sugerido, o reajuste seria de ${brl(Math.abs(difference))}${pctText}.`,
      };
    } else {
      comparison = {
        tone: "bg-[#EBF2EE] text-[#365F48] border-[#CFE0D5]",
        title: `Seu preço atual está ${brl(Math.abs(difference))} acima do preço sugerido.`,
        text: `O cálculo indica um valor sugerido ${brl(Math.abs(difference))} menor que o que você cobra hoje${pctText}.`,
      };
    }
  }

  return (
    <div className="space-y-5" data-testid="pricing-result">
      <div>
        <div className="text-xs uppercase font-bold tracking-[.16em] text-[#71717A]">Seu resultado</div>
        <h2 className="font-display text-xl md:text-2xl font-bold text-[#27272A] mt-1">
          Veja quanto esse serviço precisa gerar para pagar o negócio, remunerar seu trabalho e deixar lucro.
        </h2>
      </div>

      {warnings.length > 0 && (
        <div className="rounded-2xl border border-[#E9C9BE] bg-[#F9ECE8] p-4 text-[#8E4736]">
          <div className="font-bold text-sm">Confira estes dados antes de usar o preço:</div>
          <div className="text-sm mt-1 space-y-1">
            {warnings.map((warning, index) => <div key={index}>• {warning}</div>)}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <SummaryCard
          label="Você cobra hoje"
          value={current}
          help={current > 0 ? "Valor informado por você." : "Você não informou um preço atual."}
          muted={!current}
        />
        <SummaryCard
          label="Mínimo saudável"
          value={minimum}
          help="Cobre produtos, estrutura, seu trabalho, taxas e impostos. Sem lucro do negócio."
        />
        <div className="rounded-2xl bg-[#682F3E] text-white p-5 shadow-[0_16px_40px_rgba(104,47,62,.18)]">
          <div className="text-xs font-bold uppercase tracking-wide text-[#EBD8D2]">Preço sugerido</div>
          <div className="font-mono-brand text-3xl font-bold mt-1" data-testid="calc-price">{brl(recommended)}</div>
          <div className="text-[11px] text-[#EBD8D2] mt-2">Cobre tudo e inclui o lucro que você escolheu.</div>
        </div>
      </div>

      {comparison && (
        <div className={`rounded-2xl border p-4 ${comparison.tone}`}>
          <div className="font-bold text-sm">{comparison.title}</div>
          <div className="text-sm mt-1 opacity-90">{comparison.text}</div>
          {monthlyQty > 0 && Math.abs(monthlyImpact) >= 0.01 && (
            <div className="text-sm mt-2 font-medium">
              {monthlyImpact > 0
                ? `Mantendo cerca de ${monthlyQty} atendimentos deste serviço no mês, essa diferença representa até ${brl(Math.abs(monthlyImpact))} de faturamento mensal adicional.`
                : `Mantendo cerca de ${monthlyQty} atendimentos deste serviço no mês, seu preço atual representa ${brl(Math.abs(monthlyImpact))} a mais de faturamento em relação ao valor sugerido.`}
            </div>
          )}
        </div>
      )}

      {recommended > calculatedRaw + 0.01 && (
        <div className="rounded-xl bg-[#FAF8F6] border border-[#E8E2DE] p-3 text-xs text-[#71717A]">
          O cálculo técnico chegou a <b className="text-[#27272A]">{brl(calculatedRaw)}</b>. O NailControl sugeriu <b className="text-[#682F3E]">{brl(recommended)}</b> por arredondamento comercial para o próximo múltiplo de R$ 5.
        </div>
      )}

      {(materialBreakdown.length > 0 || disposableBreakdown.length > 0) && (
        <div className="rounded-2xl border border-[#E8E2DE] p-4">
          <h4 className="font-semibold text-sm text-[#27272A]">Custo dos produtos por atendimento</h4>
          <p className="text-xs text-[#71717A] mt-1 mb-3">Assim você consegue conferir exatamente qual item está puxando o preço para cima.</p>
          <div className="space-y-2">
            {materialBreakdown.map((item, index) => (
              <div key={`m-${index}`} className="flex items-center justify-between text-sm">
                <span className="text-[#71717A]">{item.name}</span>
                <span className="font-mono-brand text-[#27272A]">{brl(item.appointment_cost || 0)}</span>
              </div>
            ))}
            {disposableBreakdown.map((item, index) => (
              <div key={`d-${index}`} className="flex items-center justify-between text-sm">
                <span className="text-[#71717A]">{item.name} <span className="text-[10px]">(descartável)</span></span>
                <span className="font-mono-brand text-[#27272A]">{brl(item.appointment_cost || 0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-5">
        <div className="rounded-2xl border border-[#E8E2DE] p-4">
          <h4 className="font-semibold text-sm text-[#27272A] mb-3">Como chegamos nesse valor</h4>
          <RRow l={result.waste_value > 0 ? `Materiais (inclui ${result.waste_pct || 0}% para perdas)` : "Materiais usados"} v={materials} />
          <RRow l="Descartáveis" v={disposables} />
          <RRow l="Outros custos do atendimento" v={other} />
          <RRow l="Estrutura usada neste serviço" v={structure} />
          <RRow l="Pagamento pelo seu trabalho" v={labor} />
          <div className="divider-soft my-2" />
          <RRow l="Custo do atendimento" v={result.base_cost || 0} strong />
          <div className="divider-soft my-2" />
          <RRow l="Taxa de pagamento" v={result.fee_value || 0} />
          <RRow l="Impostos" v={result.tax_value || 0} />
          <RRow l="Lucro do negócio" v={profit} />
          <div className="divider-soft my-2" />
          <RRow l="Preço sugerido" v={recommended} strong />
        </div>

        <div className="rounded-2xl border border-[#E8E2DE] p-4">
          <div className="text-xs font-semibold uppercase text-[#71717A] mb-2">Composição do preço sugerido</div>
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={chartData} dataKey="value" innerRadius={45} outerRadius={76} paddingAngle={2}>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => brl(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E4E4E7" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-[#71717A] flex flex-wrap gap-x-2 gap-y-1">
            {chartData.map((d, i) => <span key={d.name}><span className="inline-block w-2 h-2 rounded-full mr-1" style={{background: COLORS[i%COLORS.length]}} />{d.name}</span>)}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-[#F5ECE8] p-5">
        <h4 className="font-display font-bold text-[#682F3E]">Para onde vai cada R$ 100 que você recebe?</h4>
        <p className="text-xs text-[#817A7D] mt-1 mb-3">Considerando o preço sugerido acima:</p>
        <div className="space-y-2">
          {per100.map(item => (
            <div key={item.name} className="flex items-center justify-between text-sm">
              <span className="text-[#5F5A5C]">{item.name}</span>
              <span className="font-mono-brand font-bold text-[#682F3E]">{brl(item.value)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-[#FAF8F6] border border-[#E8E2DE] p-4 text-sm">
        <div className="font-semibold text-[#27272A]">O que o NailControl considerou</div>
        <div className="text-[#71717A] mt-1">
          Aproximadamente <b>{result.monthly_productive_hours || 0} horas produtivas/mês</b>, estrutura de <b>{brl(result.fixed_cost_per_hour || 0)}/hora</b> e remuneração do seu trabalho de <b>{brl(result.labor_hour_value || 0)}/hora</b>.
        </div>
      </div>

      <div className="rounded-2xl bg-[#F5ECE8] p-4 text-sm text-[#682F3E]">
        <b>Importante:</b> o preço sugerido é uma referência financeira baseada nos dados informados. Posicionamento, região, experiência, demanda e estratégia comercial também podem influenciar o preço final.
      </div>

      <button onClick={onSave} className="btn-primary-nc w-full flex items-center justify-center gap-2" data-testid="save-pricing">
        <Save size={16} /> Salvar esta precificação
      </button>
    </div>
  );
}

function SummaryCard({ label, value, help, muted = false }) {
  return (
    <div className="rounded-2xl bg-[#FAF8F6] border border-[#E8E2DE] p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#71717A]">{label}</div>
      <div className={`font-mono-brand text-xl font-bold mt-1 ${muted ? "text-[#A1A1AA]" : "text-[#682F3E]"}`}>
        {muted ? "—" : brl(value)}
      </div>
      <div className="text-[11px] text-[#817A7D] mt-1">{help}</div>
    </div>
  );
}

function RRow({ l, v, strong }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={strong ? "font-bold text-[#27272A]" : "text-[#71717A]"}>{l}</span>
      <span className={`font-mono-brand ${strong ? "font-bold text-[#27272A]" : "text-[#27272A]"}`}>{brl(v)}</span>
    </div>
  );
}

function NumField({ label, value, onChange, tid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">{label}</label>
      <input inputMode="decimal" value={value} onChange={e => onChange(e.target.value)} className="input-nc" data-testid={tid} />
    </div>
  );
}
function SelectField({ label, value, onChange, tid, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className="input-nc" data-testid={tid}>{children}</select>
    </div>
  );
}
